import React from "react";

import { Box } from "@mui/material";

interface SparklineProps {
  values: number[];
  stroke?: string;
  fill?: string;
  width?: number | string;
  height?: number;
  strokeWidth?: number;
}

// Inline SVG sparkline. Keeps the bundle tiny and the brand's mono aesthetic.
// `values` are dense time-series numbers; min/max are auto-scaled.
const Sparkline: React.FC<SparklineProps> = ({
  values,
  stroke = "var(--sky-500, #2f87bf)",
  fill,
  width = "100%",
  height = 28,
  strokeWidth = 1.4,
}) => {
  const viewW = 120;
  const viewH = 24;

  if (!values.length) {
    return (
      <Box sx={{ width, height }}>
        <svg
          viewBox={`0 0 ${viewW} ${viewH}`}
          preserveAspectRatio="none"
          width="100%"
          height="100%"
        >
          <line
            x1="0"
            x2={viewW}
            y1={viewH - 1}
            y2={viewH - 1}
            stroke="var(--paper-300, #d2cab3)"
            strokeWidth="1"
            strokeDasharray="2 3"
          />
        </svg>
      </Box>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = viewW / Math.max(values.length - 1, 1);

  const points = values
    .map((v, i) => {
      const x = (i * step).toFixed(2);
      const y = (viewH - ((v - min) / range) * (viewH - 2) - 1).toFixed(2);
      return `${x},${y}`;
    })
    .join(" ");

  const areaPoints = `0,${viewH} ${points} ${viewW},${viewH}`;

  return (
    <Box sx={{ width, height, lineHeight: 0 }}>
      <svg
        viewBox={`0 0 ${viewW} ${viewH}`}
        preserveAspectRatio="none"
        width="100%"
        height="100%"
      >
        {fill && <polygon points={areaPoints} fill={fill} />}
        <polyline
          points={points}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
};

export default Sparkline;
